/**
 * API client do Chat Pharu (ADR-0077). A conversa mora no ERP (`conversas`);
 * o PharuCut consome as mesmas rotas pelo proxy dele.
 *
 *   GET    /api/chat/conversas                 lista (não lidas, última)
 *   POST   /api/chat/conversas                 direta ou grupo
 *   GET    /api/chat/conversas/geral
 *   GET    /api/chat/conversas/lote/{cut_batch_id}
 *   GET/PATCH /api/chat/conversas/{id}
 *   POST   /api/chat/conversas/{id}/participantes
 *   DELETE /api/chat/conversas/{id}/participantes/{participante_id}
 *   GET/POST /api/chat/conversas/{id}/mensagens  (?antes=<id da mensagem>)
 *   POST   /api/chat/conversas/{id}/lido
 *   POST   /api/chat/conversas/{id}/digitando
 *   PATCH/DELETE /api/chat/mensagens/{id}
 *   POST   /api/chat/mensagens/{id}/reacoes
 *   GET    /api/chat/anexos/{id}?direto=1      (o ERP entrega os bytes)
 *   GET    /api/chat/nao-lidas | pessoas | configuracao
 *   GET    /api/chat/stream                    SSE (aberto por fetch)
 */

import { ACCESS_API_URL } from "../config/api";

const PREF = "/api/chat";
const STORAGE_KEY = "pharuerp:access-session";

const dados = (payload) => (payload && payload.data !== undefined ? payload.data : payload);

export function listarConversas(request) {
  return request(`${PREF}/conversas`, { silentError: true }).then(dados);
}

export function obterConversa(request, id) {
  return request(`${PREF}/conversas/${id}`, { silentError: true }).then(dados);
}

export function conversaDoLote(request, cutBatchId) {
  return request(`${PREF}/conversas/lote/${cutBatchId}`, { silentError: true }).then(dados);
}

export function criarDireta(request, { usuarioId, operadorId }) {
  return request(`${PREF}/conversas`, {
    method: "POST",
    body: JSON.stringify({ tipo: "direta", usuario_id: usuarioId, operador_id: operadorId }),
  }).then(dados);
}

export function criarGrupo(request, { nome, usuarios, operadores }) {
  return request(`${PREF}/conversas`, {
    method: "POST",
    body: JSON.stringify({ tipo: "grupo", nome, usuarios, operadores }),
  }).then(dados);
}

export function alterarConversa(request, id, corpo) {
  return request(`${PREF}/conversas/${id}`, { method: "PATCH", body: JSON.stringify(corpo) }).then(dados);
}

export function adicionarParticipante(request, id, { usuarioId, operadorId }) {
  return request(`${PREF}/conversas/${id}/participantes`, {
    method: "POST",
    body: JSON.stringify({ usuario_id: usuarioId, operador_id: operadorId }),
  }).then(dados);
}

export function removerParticipante(request, id, participanteId) {
  return request(`${PREF}/conversas/${id}/participantes/${participanteId}`, { method: "DELETE" });
}

export function listarMensagens(request, id, antes) {
  const qs = antes ? `?antes=${encodeURIComponent(antes)}` : "";
  return request(`${PREF}/conversas/${id}/mensagens${qs}`, { silentError: true }).then(dados);
}

function corpoDaMensagem({ texto, idDoCliente, respostaA, arquivos, cartao }) {
  const corpo = new FormData();
  corpo.set("texto", texto || "");
  corpo.set("id_do_cliente", idDoCliente);
  if (respostaA) corpo.set("resposta_a", respostaA);
  // O cartão do comando `/` ({tipo, ref}) era montado pela tela e nunca saía daqui.
  if (cartao) corpo.set("cartao", JSON.stringify(cartao));
  (arquivos || []).forEach((a) => corpo.append("arquivos", a, a.name));
  return corpo;
}

/** `arquivos` são File; `idDoCliente` torna o envio idempotente. */
export function enviarMensagem(request, id, envio) {
  return request(`${PREF}/conversas/${id}/mensagens`, { method: "POST", body: corpoDaMensagem(envio), silentError: true }).then(dados);
}

/**
 * Envio com arquivo mostrando quanto já subiu (`aoProgredir(0..100)`) — o
 * `fetch` não conta o upload, o XHR conta. Sessão vencida (401) cai no envio
 * normal, que renova o token pelo caminho do `request`; reenviar é seguro pelo
 * `id_do_cliente`.
 */
export function enviarComProgresso(request, id, envio, aoProgredir) {
  if (!envio.arquivos || envio.arquivos.length === 0 || typeof XMLHttpRequest === "undefined") return enviarMensagem(request, id, envio);
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${ACCESS_API_URL}${PREF}/conversas/${id}/mensagens`);
    Object.entries(cabecalhosDaSessao()).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) aoProgredir(Math.min(99, Math.round((e.loaded / e.total) * 100))); };
    xhr.onload = () => {
      if (xhr.status === 401) { enviarMensagem(request, id, envio).then(resolve, reject); return; }
      let payload = null;
      try { payload = JSON.parse(xhr.responseText); } catch { /* corpo vazio */ }
      if (xhr.status >= 200 && xhr.status < 300) { aoProgredir(100); resolve(dados(payload)); return; }
      const erro = new Error((payload && (payload.detalhe || payload.detail)) || `Falha ao enviar (${xhr.status})`);
      erro.status = xhr.status;
      erro.payload = payload;
      reject(erro);
    };
    xhr.onerror = () => reject(new Error("Sem conexão com o servidor."));
    xhr.send(corpoDaMensagem(envio));
  });
}

const LIMITE_SEM_COMPRIMIR = 400 * 1024;

/** Foto grande vira JPEG de até 1600 px antes de subir (a mesma regra do PharuCut). */
export async function comprimirFoto(arquivo, maximo = 1600, qualidade = 0.85) {
  if (!arquivo.type.startsWith("image/") || arquivo.type === "image/gif" || arquivo.size < LIMITE_SEM_COMPRIMIR) return arquivo;
  if (typeof createImageBitmap === "undefined") return arquivo;
  try {
    const bitmap = await createImageBitmap(arquivo);
    const escala = Math.min(1, maximo / Math.max(bitmap.width, bitmap.height));
    if (escala === 1 && arquivo.size < 2 * 1024 * 1024) return arquivo;
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * escala);
    canvas.height = Math.round(bitmap.height * escala);
    canvas.getContext("2d")?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", qualidade));
    if (!blob || blob.size >= arquivo.size) return arquivo;
    return new File([blob], `${arquivo.name.replace(/\.[^.]+$/, "")}.jpg`, { type: "image/jpeg" });
  } catch {
    return arquivo;
  }
}

export function editarMensagem(request, mensagemId, texto) {
  return request(`${PREF}/mensagens/${mensagemId}`, { method: "PATCH", body: JSON.stringify({ texto }) }).then(dados);
}

export function apagarMensagem(request, mensagemId) {
  return request(`${PREF}/mensagens/${mensagemId}`, { method: "DELETE" }).then(dados);
}

export function reagir(request, mensagemId, emoji) {
  return request(`${PREF}/mensagens/${mensagemId}/reacoes`, { method: "POST", body: JSON.stringify({ emoji }) }).then(dados);
}

/** Tocou o áudio de outra pessoa: o microfone dela fica azul (idempotente no servidor). */
export function marcarAudioOuvido(request, anexoId) {
  return request(`${PREF}/anexos/${anexoId}/ouvido`, { method: "POST", body: "{}", silentError: true }).then(dados);
}

/** O anexo de áudio passa a ouvido nesta tela, sem esperar o servidor. */
export function marcarOuvidoNaMensagem(mensagens, mensagemId, anexoId) {
  return mensagens.map((m) => (m.id !== mensagemId ? m : { ...m, anexos: (m.anexos || []).map((a) => (a.id === anexoId ? { ...a, ouvido: true } : a)) }));
}

export function marcarLido(request, id, ate) {
  return request(`${PREF}/conversas/${id}/lido`, { method: "POST", body: JSON.stringify(ate ? { ate } : {}), silentError: true }).then(dados);
}

export function avisarDigitando(request, id) {
  return request(`${PREF}/conversas/${id}/digitando`, { method: "POST", body: "{}", silentError: true });
}

export function naoLidas(request) {
  return request(`${PREF}/nao-lidas`, { silentError: true }).then(dados);
}

export function pessoas(request) {
  return request(`${PREF}/pessoas`, { silentError: true }).then(dados);
}

// ---------------------------------------------------------------- Fase 2

export function cartao(request, tipo, ref) {
  return request(`${PREF}/cartoes/${encodeURIComponent(tipo)}/${encodeURIComponent(ref)}`, { silentError: true }).then(dados);
}

export function buscar(request, termo) {
  return request(`${PREF}/busca?q=${encodeURIComponent(termo)}`, { silentError: true }).then(dados);
}

export function mencoes(request) {
  return request(`${PREF}/mencoes`, { silentError: true }).then(dados);
}

export function marcarPresenca(request) {
  return request(`${PREF}/presenca`, { method: "POST", body: "{}", silentError: true });
}

/** Os comandos `/` (o mesmo catálogo do PharuCut). Cartões de lote/plano/fila/atrasados
 *  vivem no Cut; o ERP só desenha o que é dele. */
export const COMANDOS = [
  { nome: "material", aliases: ["estoque", "mat"], tipo: "material", pedeArgumento: true, exemplo: "/material Branco TX 15", descricao: "Saldo físico, reservado e disponível", permissao: "estoque:view" },
  { nome: "sobra", aliases: ["sobras"], tipo: "sobra", pedeArgumento: true, exemplo: "/sobra Branco TX", descricao: "Sobras disponíveis do material", permissao: "cut_sobra:view" },
  { nome: "requisição", aliases: ["requisicao", "req"], tipo: "requisicao", pedeArgumento: true, exemplo: "/requisição REQ-UN1-43", descricao: "A requisição na fila do comprador", permissao: "compras:view" },
  { nome: "pedido", aliases: ["pv"], tipo: "pedido", pedeArgumento: true, exemplo: "/pedido 1018", descricao: "Situação, valor, entrega e ambientes do pedido", permissao: "pedido:view" },
  { nome: "op", aliases: ["ordem"], tipo: "op", pedeArgumento: true, exemplo: "/op 245", descricao: "Etapa atual, prazo e o que falta na produção", permissao: "ordem_producao:view" },
  { nome: "entrega", aliases: ["carga"], tipo: "entrega", pedeArgumento: true, exemplo: "/entrega 12", descricao: "Data prevista, cliente e carga da entrega", permissao: "logistica:view" },
  { nome: "lote", aliases: ["l"], tipo: "lote", pedeArgumento: true, exemplo: "/lote 479", descricao: "Cartão do lote (dado do PharuCut)", permissao: null },
  { nome: "fila", aliases: [], tipo: "fila", pedeArgumento: false, exemplo: "/fila", descricao: "A fila de corte (dado do PharuCut)", permissao: null },
  { nome: "atrasados", aliases: ["lotes atrasados"], tipo: "atrasados", pedeArgumento: false, exemplo: "/atrasados", descricao: "Lotes com prazo de corte vencido (PharuCut)", permissao: null },
];

const normalizar = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export function lerComando(texto) {
  const linha = String(texto || "").trimStart();
  if (!linha.startsWith("/")) return null;
  const [primeira, ...resto] = linha.split("\n");
  const corpo = primeira.slice(1).trim();
  const chave = normalizar(corpo);
  for (const c of COMANDOS) {
    const nomes = [c.nome, ...c.aliases].map(normalizar).sort((a, b) => b.length - a.length);
    for (const n of nomes) {
      if (chave === n || chave.startsWith(`${n} `)) {
        const argumento = corpo.slice(n.length).trim();
        return { comando: c, argumento: c.pedeArgumento ? argumento : "", legenda: resto.join("\n").trim() || (c.pedeArgumento ? "" : argumento) };
      }
    }
  }
  return null;
}

export function sugerirComandos(texto, pode) {
  const linha = String(texto || "").trimStart();
  if (!linha.startsWith("/") || linha.includes("\n")) return [];
  const digitado = normalizar(linha.slice(1)).split(" ")[0];
  return COMANDOS.filter((c) => !c.permissao || pode(c.permissao)).filter((c) => [c.nome, ...c.aliases].some((n) => normalizar(n).startsWith(digitado)));
}

export function mencaoEmAndamento(texto, cursor) {
  const antes = String(texto || "").slice(0, cursor);
  const m = /(?:^|\s)@([\wÀ-ÿ.-]*)$/u.exec(antes);
  if (!m) return null;
  return { inicio: cursor - m[1].length - 1, termo: m[1] };
}

export function sugerirPessoas(termo, nomes) {
  const t = normalizar(termo);
  return nomes.filter((n) => normalizar(n).startsWith(t) || normalizar(n).split(" ").some((p) => p.startsWith(t))).slice(0, 6);
}

export function preferenciasDoChat(request) {
  return request(`${PREF}/preferencias`, { silentError: true }).then(dados);
}

export function salvarPreferenciasDoChat(request, corpo) {
  return request(`${PREF}/preferencias`, { method: "PUT", body: JSON.stringify(corpo), silentError: true }).then(dados);
}

export function configuracaoDoChat(request) {
  return request(`${PREF}/configuracao`, { silentError: true }).then(dados);
}

export function novoIdDoCliente() {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function tokenDaSessao() {
  try {
    const bruto = window.localStorage.getItem(STORAGE_KEY);
    return bruto ? JSON.parse(bruto)?.accessToken || null : null;
  } catch {
    return null;
  }
}

function cabecalhosDaSessao() {
  const token = tokenDaSessao();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Os do balão são miniaturas de ~25 KB: 200 cabem folgados. O que sai é o
// MENOS USADO (cada uso move para o fim) — antes saía o mais antigo, ainda na
// tela, e a foto ficava quebrada em conversa com muita mídia.
const cacheDeBlob = new Map();
const MAXIMO_EM_MEMORIA = 200;

// Token vencido: a tela registra como renovar a sessão (o `request` do AuthContext).
let renovarSessao = null;
export function definirRenovadorDeSessao(fn) {
  renovarSessao = fn;
}

const revogar = (promessa) => promessa.then((url) => URL.revokeObjectURL(url)).catch(() => undefined);

/** Trocou a pessoa (ou o chat fechou): arquivo aberto por um não fica na memória para o seguinte. */
export function limparAnexos() {
  cacheDeBlob.forEach(revogar);
  cacheDeBlob.clear();
}

/**
 * O tipo do `blob:` é decidido AQUI, pelo tipo do anexo: o `blob:` tem a origem
 * do app, e um HTML aberto nele leria o token de quem clicou.
 */
export function tipoSeguro(anexo, daResposta) {
  const mime = String(daResposta || "").toLowerCase().split(";")[0].trim();
  if (anexo.tipo === "pdf") return "application/pdf";
  if (anexo.tipo === "imagem" && mime.startsWith("image/") && mime !== "image/svg+xml") return mime;
  if (anexo.tipo === "video" && mime.startsWith("video/")) return mime;
  if (anexo.tipo === "audio" && mime.startsWith("audio/")) return mime;
  return "application/octet-stream";
}

/**
 * URL `blob:` do anexo, baixado com a sessão (o ERP confere a participação e
 * entrega os bytes). `miniatura`: a foto pequena do balão — o original só
 * desce quando a pessoa abre a foto.
 */
export function blobDoAnexo(anexo, { miniatura = false } = {}) {
  const chave = miniatura ? `${anexo.id}:mini` : anexo.id;
  const existente = cacheDeBlob.get(chave);
  if (existente) {
    cacheDeBlob.delete(chave);
    cacheDeBlob.set(chave, existente);
    return existente;
  }
  const consulta = miniatura ? "?miniatura=1" : "";
  const baixar = () => fetch(`${ACCESS_API_URL}${PREF}/anexos/${anexo.id}${consulta}`, { headers: cabecalhosDaSessao(), cache: "no-store" });
  const promessa = baixar().then(async (primeira) => {
    let r = primeira;
    // Sessão de 15 min: renova pelo caminho do `request` e tenta uma vez mais.
    if (r.status === 401 && renovarSessao) {
      await Promise.resolve(renovarSessao()).catch(() => undefined);
      r = await baixar();
    }
    if (!r.ok) throw new Error(`arquivo ${r.status}`);
    return URL.createObjectURL(new Blob([await r.arrayBuffer()], { type: tipoSeguro(anexo, r.headers.get("content-type")) }));
  });
  promessa.catch(() => cacheDeBlob.delete(chave));
  cacheDeBlob.set(chave, promessa);
  if (cacheDeBlob.size > MAXIMO_EM_MEMORIA) {
    const [maisAntigo, url] = cacheDeBlob.entries().next().value;
    cacheDeBlob.delete(maisAntigo);
    revogar(url);
  }
  return promessa;
}

/** Puro: quais conversas um aviso manda reler, dado o que a tela já conhecia
 *  (`null` = primeiro aviso da pessoa: vale a conta do servidor). */
export function conversasQueMudaram(aviso, conhecidas) {
  if (!aviso.marcas || !conhecidas) return aviso.conversas || [];
  const mudaram = Object.keys(aviso.marcas).filter((id) => conhecidas[id] !== aviso.marcas[id]);
  const sumiram = Object.keys(conhecidas).filter((id) => !(id in aviso.marcas));
  return [...mudaram, ...sumiram];
}

// ---------------------------------------------------------------- stream v2 (pacote)
//
// O aviso `conversas` traz, por conversa que mudou: as mensagens novas ou
// alteradas já prontas, quem leu até onde (`leituras`), a prévia da lista e as
// não lidas. A tela aplica isso no que já tem — sem reler nada pela API.

const PREFIXO_PENDENTE = "pendente:";

/**
 * A mensagem que a pessoa acabou de mandar, na tela antes do servidor responder.
 * `anexos`: [{ nome, tipo, tamanho, urlLocal }] — `urlLocal` mostra a foto na hora.
 */
export function mensagemPendente({ idDoCliente, conversaId, eu, texto, anexos = [], respostaA = null, cartao = null }) {
  return {
    id: `${PREFIXO_PENDENTE}${idDoCliente}`,
    id_do_cliente: idDoCliente,
    conversa_id: conversaId,
    autor_id: eu?.id || null,
    autor_nome: eu?.nome || "",
    autor_tipo: eu?.tipo || "usuario",
    minha: true,
    texto: texto || "",
    apagada: false,
    editada_em: null,
    criada_em: new Date().toISOString(),
    resposta_a: respostaA ? { id: respostaA.id, autor_nome: respostaA.autor_nome, texto: (respostaA.texto || "").slice(0, 140) } : null,
    anexos: anexos.map((a, i) => ({
      id: `${PREFIXO_PENDENTE}${idDoCliente}:${i}`,
      nome: a.nome,
      tipo: a.tipo || "outro",
      tamanho: a.tamanho,
      local_url: a.urlLocal || null,
    })),
    reacoes: [],
    cartao,
    pendente: "enviando",
  };
}

export const ehPendente = (m) => typeof m?.id === "string" && m.id.startsWith(PREFIXO_PENDENTE);

/** Quem leu cada mensagem MINHA, a partir de até onde cada pessoa leu. */
export function aplicarLeituras(mensagens, leituras) {
  if (!leituras) return mensagens;
  return mensagens.map((m) => {
    if (!m.minha || ehPendente(m)) return m;
    const criada = Date.parse(m.criada_em);
    const alcancou = (iso) => !!iso && Date.parse(iso) >= criada;
    const outros = leituras.filter((l) => l.id !== m.autor_id);
    const lido_por = outros
      .filter((l) => alcancou(l.em))
      .map((l) => ({ id: l.id, nome: l.nome, em: l.em }))
      .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
    if (outros.length === 0) return { ...m, lido_por };
    return {
      ...m,
      lido_por,
      // ✓✓ cinza (chegou a todos) e ✓✓ azul (todos leram) — Fase B.
      entregue_a_todos: outros.every((l) => alcancou(l.entregue) || alcancou(l.em)),
      lido_por_todos: outros.every((l) => alcancou(l.em)),
    };
  });
}

/**
 * Fase B — a marca da MINHA mensagem, como no WhatsApp:
 * "enviando" (relógio), "falhou", "enviada" (✓), "entregue" (✓✓) e "lida" (✓✓ azul).
 */
export function marcaDeEnvio(m) {
  if (!m?.minha) return null;
  if (m.pendente === "falhou") return "falhou";
  if (ehPendente(m)) return "enviando";
  if (m.lido_por_todos) return "lida";
  if (m.entregue_a_todos) return "entregue";
  return "enviada";
}

/** O título da aba com as não lidas das conversas que não estão silenciadas. */
export function totalNaoLidas(conversas) {
  return (conversas || []).filter((c) => !c.silenciada).reduce((soma, c) => soma + (c.nao_lidas || 0), 0);
}

export function tituloComContagem(base, total) {
  return total > 0 ? `(${total > 99 ? "99+" : total}) ${base}` : base;
}

/** Copia o texto da mensagem (Fase B). Devolve se deu certo. */
export async function copiarTexto(texto) {
  try {
    await navigator.clipboard.writeText(texto || "");
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------- Fase B e C (rotas)

export function definirMinhaConversa(request, id, corpo) {
  return request(`${PREF}/conversas/${id}/minha`, { method: "PATCH", body: JSON.stringify(corpo) }).then(dados);
}

/** Apaga a conversa só para mim: some da minha lista; a outra pessoa não perde nada. */
export function apagarConversaParaMim(request, id) {
  return request(`${PREF}/conversas/${id}/minha`, { method: "DELETE" }).then(dados);
}

/** O stream trouxe conversa que a lista não tem: era uma que apaguei e recebeu mensagem. */
/** Mensagem "nova" para o som: do último minuto (o stream reabre os últimos segundos). */
const JANELA_DO_SOM_MS = 60000;

/**
 * A regra do som de mensagem nova — a MESMA no ERP e no Cut (2026-11-03):
 * toca para mensagem de OUTRA pessoa, nem editada nem apagada, numa conversa
 * que não está silenciada e que a pessoa não está olhando agora. Devolve os
 * ids que tocariam; quem chama guarda os que já tocaram (uma reação reenvia
 * a mensagem no stream e não pode tocar de novo).
 */
export function mensagensQueTocam(pacote, { conversaAberta = null, abaOculta = false, silenciadas = new Set(), agora = Date.now() } = {}) {
  const recente = agora - JANELA_DO_SOM_MS;
  return (pacote?.conversas || []).flatMap((i) => {
    const silenciada = i.silenciada ?? silenciadas.has(i.conversa_id);
    const olhando = !abaOculta && i.conversa_id === conversaAberta;
    if (silenciada || olhando) return [];
    return (i.mensagens || [])
      .filter((m) => !m.minha && !m.editada_em && !m.apagada && Date.parse(m.criada_em) > recente)
      .map((m) => m.id);
  });
}

export function temConversaForaDaLista(conversas, pacote) {
  const conhecidas = new Set(conversas.map((c) => c.id));
  return (pacote?.conversas || []).some((i) => !conhecidas.has(i.conversa_id));
}

/**
 * A direta com um PIN da fábrica sem vínculo que parece ser um usuário do ERP:
 * a mesma pessoa em duas conversas (as duas "Joices" de 2026-10-29).
 */
export function mesmaPessoaDaDireta(detalhe, pessoasDaUnidade) {
  if (detalhe?.tipo !== "direta") return null;
  const outro = (detalhe.participantes || []).find((p) => !p.saiu_em && p.id !== detalhe.eu?.id);
  if (outro?.tipo !== "operador") return null;
  const operador = (pessoasDaUnidade?.operadores || []).find((o) => o.id === outro.operador_id);
  return operador?.parece_ser ? { operador, usuario: operador.parece_ser } : null;
}

/** "operador" ou, sem vínculo e com um usuário de nome igual, "operador · parece ser Fulano". */
export function detalheDoOperador(operador) {
  const base = operador.funcao ? `${operador.funcao} · operador` : "operador";
  return operador.parece_ser ? `${base} · parece ser ${operador.parece_ser.nome}` : base;
}

export function definirAdministrador(request, conversaId, participanteId, administrador) {
  return request(`${PREF}/conversas/${conversaId}/participantes/${participanteId}`, { method: "PATCH", body: JSON.stringify({ administrador }) }).then(dados);
}

export function encaminharMensagem(request, mensagemId, conversas) {
  return request(`${PREF}/mensagens/${mensagemId}/encaminhar`, { method: "POST", body: JSON.stringify({ conversas }) }).then(dados);
}

/** Manda de novo o aviso a quem ainda não leu a minha mensagem — devolve {avisados: [nomes]}. */
export function notificarDeNovo(request, mensagemId) {
  return request(`${PREF}/mensagens/${mensagemId}/notificar`, { method: "POST", body: "{}" }).then(dados);
}

/** "Notificar novamente" só faz sentido na minha mensagem já enviada que alguém ainda não leu. */
export function podeNotificarDeNovo(m) {
  return Boolean(m?.minha && !m.apagada && !m.pendente && m.lido_por_todos === false);
}

/** "Avisamos a Ana de novo." / "Avisamos 3 pessoas de novo." */
export function textoDoLembrete(avisados = []) {
  if (avisados.length === 1) return `Avisamos ${avisados[0]} de novo.`;
  return `Avisamos ${avisados.length} pessoas de novo.`;
}

export function fixarMensagem(request, mensagemId, fixar) {
  return request(`${PREF}/mensagens/${mensagemId}/fixar`, { method: fixar ? "POST" : "DELETE", body: "{}" }).then(dados);
}

export function listarAgendadas(request, conversaId) {
  return request(`${PREF}/conversas/${conversaId}/agendadas`, { silentError: true }).then(dados);
}

export function agendarMensagem(request, conversaId, { texto, enviarEm }) {
  return request(`${PREF}/conversas/${conversaId}/agendadas`, { method: "POST", body: JSON.stringify({ texto, enviar_em: enviarEm }) }).then(dados);
}

export function cancelarAgendada(request, id) {
  return request(`${PREF}/agendadas/${id}`, { method: "DELETE" }).then(dados);
}

export function virarTarefa(request, mensagemId, { titulo, responsavelId, prazo }) {
  return request(`${PREF}/mensagens/${mensagemId}/tarefa`, {
    method: "POST",
    body: JSON.stringify({ titulo, responsavel_id: responsavelId || null, prazo: prazo || null }),
  }).then(dados);
}

export function perguntarAoAssistente(request, pergunta) {
  return request(`${PREF}/assistente`, { method: "POST", body: JSON.stringify({ pergunta }), silentError: true }).then(dados);
}

export function salvarConfiguracaoDoChat(request, corpo) {
  return request(`${PREF}/configuracao`, { method: "PUT", body: JSON.stringify(corpo) }).then(dados);
}

/**
 * Junta mensagens confirmadas pelo servidor ao que está na tela: troca a que já
 * existia (mesmo id), troca a pendente pela confirmada (mesmo `id_do_cliente`)
 * e mantém as pendentes no fim, na ordem em que foram escritas.
 */
export function juntarMensagens(atuais, chegaram) {
  if (!chegaram || chegaram.length === 0) return atuais;
  const porId = new Map(chegaram.map((m) => [m.id, m]));
  const clientes = new Set(chegaram.map((m) => m.id_do_cliente).filter(Boolean));
  const confirmadas = atuais.filter((m) => !ehPendente(m) && !porId.has(m.id));
  const pendentes = atuais.filter((m) => ehPendente(m) && !clientes.has(m.id_do_cliente));
  const ordenadas = [...confirmadas, ...chegaram].sort((a, b) => Date.parse(a.criada_em) - Date.parse(b.criada_em));
  return [...ordenadas, ...pendentes];
}

/** O item do pacote aplicado às mensagens da conversa aberta. */
export function aplicarPacoteNasMensagens(atuais, item) {
  return aplicarLeituras(juntarMensagens(atuais, item.mensagens), item.leituras);
}

/** A lista de conversas com a prévia, a hora e as não lidas do pacote. */
export function aplicarPacoteNaLista(conversas, pacote) {
  const porId = new Map((pacote?.conversas || []).map((i) => [i.conversa_id, i]));
  if (porId.size === 0) return conversas;
  return conversas.map((c) => {
    const item = porId.get(c.id);
    if (!item) return c;
    return { ...c, nao_lidas: item.nao_lidas, ultima: item.ultima, ultima_mensagem_em: item.ultima_mensagem_em };
  });
}

/**
 * Abre o stream SSE com o token da sessão — `EventSource` não manda
 * Authorization. Reconecta com espera crescente até `parar()`.
 *
 * - `aoAviso(aviso)`: `aviso.mudaram` são as conversas a reler (a marca de cada
 *   conversa atravessa a reconexão: reabrir o stream não relê a tela inteira);
 * - `aoPacote(pacote)`: com ele, o stream é o v2 — o que mudou chega pronto no
 *   aviso `conversas` (e a marca de cada conversa nele conta como vista);
 * - `aoDigitando(mapa)`: quem digita, sem tocar em mais nada;
 * - `aoEstado("conectado"|"reconectando")`;
 * - `aoSemAcesso(status)`: 401/403 — a tela renova a sessão pelo caminho dela.
 *
 * O servidor bate a cada 20 s: 45 s sem um byte é conexão morta (aborta e
 * reconecta). `event: fim` é o servidor encerrando para reconferir o acesso:
 * reabre na hora.
 */
const esperar = (ms) => new Promise((r) => setTimeout(r, ms));
const SILENCIO_MAXIMO_MS = 45000;

export function abrirStream({ aoAviso, aoEstado, aoPacote = null, aoDigitando = () => undefined, aoSemAcesso = () => undefined }) {
  const rota = aoPacote ? `${PREF}/stream?v=2` : `${PREF}/stream`;
  let cancelado = false;
  let conectado = false;
  let controle = null;
  let espera = 1000;
  let ultimoByte = Date.now();
  let marcas = null;

  const vigia = setInterval(() => {
    if (conectado && Date.now() - ultimoByte > SILENCIO_MAXIMO_MS && controle) controle.abort();
  }, 5000);

  const laco = async () => {
    while (!cancelado) {
      controle = new AbortController();
      let encerradoPeloServidor = false;
      try {
        const resposta = await fetch(`${ACCESS_API_URL}${rota}`, { headers: cabecalhosDaSessao(), signal: controle.signal, cache: "no-store" });
        if (resposta.status === 401 || resposta.status === 403) await Promise.resolve(aoSemAcesso(resposta.status)).catch(() => undefined);
        if (!resposta.ok || !resposta.body) throw new Error(`stream ${resposta.status}`);
        espera = 1000;
        conectado = true;
        ultimoByte = Date.now();
        aoEstado("conectado");
        const leitor = resposta.body.getReader();
        const decodificador = new TextDecoder();
        let pendente = "";
        let aberto = true;
        while (aberto) {
          const { done, value } = await leitor.read();
          if (done || cancelado) break;
          ultimoByte = Date.now();
          pendente += decodificador.decode(value, { stream: true }).replace(/\r\n/g, "\n");
          const blocos = pendente.split("\n\n");
          pendente = blocos.pop() || "";
          for (const bloco of blocos) {
            const linhas = bloco.split("\n");
            const evento = (linhas.find((l) => l.startsWith("event: ")) || "").slice(7);
            const linha = linhas.find((l) => l.startsWith("data: "));
            if (evento === "fim") { encerradoPeloServidor = true; aberto = false; break; }
            if (!["mudou", "digitando", "conversas"].includes(evento) || !linha) continue;
            try {
              const aviso = JSON.parse(linha.slice(6));
              if (evento === "digitando") aoDigitando(aviso.digitando || {});
              else if (evento === "conversas") {
                if (!aoPacote) continue;
                // O que veio no pacote já está na tela: a reconexão não relê.
                const vistasAgora = Object.fromEntries((aviso.conversas || []).map((item) => [item.conversa_id, item.marca]));
                marcas = { ...(marcas || {}), ...vistasAgora };
                aoPacote(aviso);
              } else {
                const mudaram = conversasQueMudaram(aviso, marcas);
                if (aviso.marcas) marcas = aviso.marcas;
                aoAviso({ ...aviso, mudaram });
              }
            } catch {
              /* aviso malformado: o próximo corrige */
            }
          }
        }
        leitor.cancel().catch(() => undefined);
      } catch {
        /* rede caiu, o vigia abortou ou o servidor fechou: reconecta abaixo */
      }
      conectado = false;
      if (cancelado) return;
      if (encerradoPeloServidor) continue;
      aoEstado("reconectando");
      await esperar(espera);
      espera = Math.min(espera * 2, 30000);
    }
  };
  laco();
  return () => {
    cancelado = true;
    clearInterval(vigia);
    if (controle) controle.abort();
  };
}

export const ROTULO_DO_TIPO = { imagem: "Foto", video: "Vídeo", audio: "Áudio", pdf: "PDF", documento: "Documento", outro: "Arquivo" };
export const SIGLA_DO_TIPO = { imagem: "FOT", video: "VID", audio: "AUD", pdf: "PDF", documento: "DOC", outro: "ARQ" };

export function tamanhoLegivel(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MB`;
}

export function horaCurta(iso) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

export function quandoCurto(iso, agora = new Date()) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  if (d.toDateString() === agora.toDateString()) return horaCurta(iso);
  const ontem = new Date(agora);
  ontem.setDate(agora.getDate() - 1);
  if (d.toDateString() === ontem.toDateString()) return "ontem";
  if ((agora - d) / 86400000 < 6) return d.toLocaleDateString("pt-BR", { weekday: "short" }).replace(".", "");
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

export function rotuloDoDia(iso, agora = new Date()) {
  const d = new Date(iso);
  if (d.toDateString() === agora.toDateString()) return "Hoje";
  const ontem = new Date(agora);
  ontem.setDate(agora.getDate() - 1);
  if (d.toDateString() === ontem.toDateString()) return "Ontem";
  return d.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "2-digit" });
}

/** Mensagens em ordem, partidas por dia local. */
export function agruparPorDia(mensagens, agora = new Date()) {
  const grupos = [];
  mensagens.forEach((m) => {
    const dia = new Date(m.criada_em).toDateString();
    const ultimo = grupos[grupos.length - 1];
    if (ultimo && ultimo.dia === dia) ultimo.mensagens.push(m);
    else grupos.push({ dia, rotulo: rotuloDoDia(m.criada_em, agora), mensagens: [m] });
  });
  return grupos;
}

/** A ordem da lista aprovada na maquete: lotes em corte, grupos (geral primeiro), diretas, arquivados. */
const MAXIMO_DE_CONTATOS = 8;

/**
 * A busca da lista também acha CONTATOS (pedido do Igor, 2026-10-28): quem
 * bate com o termo e ainda não tem conversa direta aparece para abrir uma num
 * clique, sem passar por "Nova". Sem acento e sem maiúscula, como a busca.
 */
export function contatosDaBusca(pessoasDaUnidade, termo, conversas = []) {
  const alvo = normalizar(termo).trim();
  if (!alvo || !pessoasDaUnidade) return [];
  const comDireta = new Set(conversas.filter((c) => c.tipo === "direta").map((c) => normalizar(c.nome)));
  const todas = [
    ...(pessoasDaUnidade.usuarios || []).map((p) => ({ id: p.id, tipo: "usuario", nome: p.nome, detalhe: p.cargo || "", foto_url: p.foto_url || null })),
    ...(pessoasDaUnidade.operadores || []).map((p) => ({ id: p.id, tipo: "operador", nome: p.nome, detalhe: detalheDoOperador(p) })),
  ];
  return todas
    .filter((p) => normalizar(p.nome).includes(alvo) && !comDireta.has(normalizar(p.nome)))
    .slice(0, MAXIMO_DE_CONTATOS);
}

export function agruparConversas(conversas, busca = "") {
  const termo = busca.trim().toLowerCase();
  const visiveis = termo ? conversas.filter((c) => `${c.nome} ${c.batch_code || ""}`.toLowerCase().includes(termo)) : conversas;
  const porData = (a, b) => (b.ultima_mensagem_em || "").localeCompare(a.ultima_mensagem_em || "");
  const lotes = visiveis.filter((c) => c.tipo === "lote" && !c.arquivada_em).sort(porData);
  const grupos = visiveis
    .filter((c) => c.tipo === "grupo" || c.tipo === "geral")
    .sort((a, b) => (a.tipo === "geral" ? -1 : b.tipo === "geral" ? 1 : porData(a, b)));
  const diretas = visiveis.filter((c) => c.tipo === "direta").sort(porData);
  const arquivadas = visiveis.filter((c) => c.tipo === "lote" && c.arquivada_em).sort(porData);
  // Fase B: a pessoa fixou no topo — sai do grupo de origem e sobe.
  const fixadas = visiveis.filter((c) => c.fixada && !c.arquivada_em).sort(porData);
  const semFixadas = (lista) => lista.filter((c) => !c.fixada);
  return [
    { rotulo: "Fixadas", conversas: fixadas },
    { rotulo: "Lotes em corte", conversas: semFixadas(lotes) },
    { rotulo: "Grupos", conversas: semFixadas(grupos) },
    { rotulo: "Diretas", conversas: semFixadas(diretas) },
    { rotulo: "Lotes arquivados", conversas: arquivadas },
  ].filter((g) => g.conversas.length > 0);
}

export function iniciais(nome) {
  const partes = String(nome || "?").trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return "?";
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
}

export function corDoAvatar(nome) {
  let h = 0;
  for (const c of String(nome || "")) h = (h * 31 + c.charCodeAt(0)) % 6;
  return h;
}
